import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '@bloomneo/uikit';
import { config } from '../config';
import { clearCache } from '../../../shared/lib/cache';
import type { AuthState, LoginCredentials, RegisterData, AuthContextType, VerifyEmailData, ForgotPasswordData, ResetPasswordData } from '../types';

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Decode a JWT's `exp` claim and return it as a millisecond epoch, or
 * null when the token is missing/malformed/has no exp. Pure client-side
 * read of the (unverified) payload — used only to know WHEN to drop a
 * session locally; the server still verifies the signature on every call.
 */
function getTokenExpMs(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join(''),
      ),
    );
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const api = useApi({
    baseURL: config.api.baseUrl,
    headers: {
      [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue
    }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      const token = localStorage.getItem(config.auth.storage.token);
      const userData = localStorage.getItem(config.auth.storage.user);

      if (token && userData) {
        try {
          const user = JSON.parse(userData);
          // 30-day session: if the JWT has already expired, clear it and
          // stay logged out rather than booting into an authenticated UI
          // that will 401 on the first request.
          const expMs = getTokenExpMs(token);
          if (expMs && Date.now() >= expMs) {
            localStorage.removeItem(config.auth.storage.token);
            localStorage.removeItem(config.auth.storage.user);
            setAuthState(prev => ({ ...prev, user: null, token: null, isAuthenticated: false, isLoading: false }));
            return;
          }
          setAuthState({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          localStorage.removeItem(config.auth.storage.token);
          localStorage.removeItem(config.auth.storage.user);
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    // Initialize on mount
    initializeAuth();

    // Listen for localStorage changes (from UserContext profile updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === config.auth.storage.user && e.newValue) {
        try {
          const updatedUser = JSON.parse(e.newValue);
          setAuthState(prev => ({
            ...prev,
            user: updatedUser
          }));
        } catch (error) {
          console.error('Error parsing updated user data:', error);
        }
      }
    };

    // Also listen for custom events for same-tab updates (e.g. student login)
    const handleCustomUserUpdate = (e: CustomEvent) => {
      if (e.detail?.user) {
        const token = localStorage.getItem(config.auth.storage.token);
        setAuthState(prev => ({
          ...prev,
          user: e.detail.user,
          ...(token ? { token, isAuthenticated: true } : {}),
        }));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userUpdated', handleCustomUserUpdate as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleCustomUserUpdate as EventListener);
    };
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> => {
    try {
      // Don't set isLoading: true to prevent unnecessary re-renders
      // The LoginPage handles its own loading state with isSubmitting
      //
      // Deliberately NOT api.post(): uikit's useApi throws `HTTP <status>:
      // <statusText>` on any non-2xx WITHOUT reading the response body, which
      // throws away the server's `message`. Sign-in is the one place that
      // message is the entire point — "no account exists for this email" and
      // "wrong password" need different actions from the person reading it,
      // and both were arriving as an indistinguishable "HTTP 400: Bad
      // Request". So this call reads the body itself.
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 15000);
      let response: any;
      try {
        const res = await fetch(`${config.api.baseUrl}${config.api.endpoints.login}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
          },
          body: JSON.stringify(credentials),
          signal: ctrl.signal,
        });
        response = await res.json().catch(() => ({}));
      } finally {
        clearTimeout(timer);
      }

      if (response.token && response.message === "Login successful") {
        const { user, token } = response;

        // Purge any prior account's cached data before this session starts.
        await clearCache();

        localStorage.setItem(config.auth.storage.token, token);
        localStorage.setItem(config.auth.storage.user, JSON.stringify(user));

        setAuthState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });

        // Support agents (moderator.approve) are platform-tier with no tenant,
        // so the shared afterLogin (/dashboard) drops them on a tenant surface
        // they cannot use. Land them on the ticket queue — the one page
        // PlatformLayoutRoute lets them reach.
        const u = user as unknown as { role?: string; level?: string } | null;
        const supportHome = u?.role === 'moderator' && u?.level === 'approve' ? '/platform/support' : null;
        const intendedPath = (location.state as any)?.from?.pathname
          || supportHome
          || config.auth.redirects.afterLogin;
        navigate(intendedPath, { replace: true });

        return { success: true };
      } else {
        // Don't change state on failed login to prevent re-renders
        return { success: false, error: response.message || 'Login failed' };
      }
    } catch (error: any) {
      // Don't change state on login error to prevent re-renders
      return {
        success: false,
        error: error?.name === 'AbortError'
          ? 'The server took too long to respond. Please check your connection and try again.'
          : error.message || 'Network error occurred'
      };
    }
  }, [navigate, location]);

  const register = useCallback(async (data: RegisterData): Promise<{ success: boolean; error?: string }> => {
    try {
      // Don't set isLoading: true to prevent unnecessary re-renders
      // The RegisterPage handles its own loading state with isSubmitting
      const response = await api.post(config.api.endpoints.register, data);

      if (response.message === "User registered successfully") {
        // Registration successful - don't auto-login, let user verify email
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Registration failed' };
      }
    } catch (error: any) {
      // Don't change state on registration error to prevent re-renders
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, [api]);

  const logout = useCallback((redirectTo: string = config.auth.redirects.afterLogout) => {
    localStorage.removeItem(config.auth.storage.token);
    localStorage.removeItem(config.auth.storage.user);

    // Clear per-user thread cache so the next user doesn't see stale threads.
    const threadIndex = localStorage.getItem('fb-threads');
    if (threadIndex) {
      try {
        const ids: { id: string }[] = JSON.parse(threadIndex);
        ids.forEach(({ id }) => localStorage.removeItem(`fb-thread-${id}`));
      } catch {}
    }
    localStorage.removeItem('fb-threads');

    // Purge the per-user IndexedDB cache so the next account on this device
    // never sees the previous one's data.
    void clearCache();

    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });

    // Full navigation (not client-side) so the destination is served fresh by
    // the server — on white-label deployments '/' is the server's branded
    // homepage, not the SPA's generic marketing route (which would flash the
    // wrong brand). Also guarantees no stale in-memory state survives logout.
    window.location.replace(redirectTo);
  }, []);

  // Auto-logout when the 30-day session token expires. We decode the JWT
  // `exp` and (a) poll on a 60s interval and (b) re-check whenever the tab
  // regains focus / becomes visible (covers laptop sleep, where timers
  // stall). A single setTimeout can't span 30 days anyway (32-bit ms
  // overflow at ~24.8 days), so polling is the reliable approach. On
  // expiry we drop the session and route to the role-appropriate login.
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.token) return;
    const expMs = getTokenExpMs(authState.token);
    if (!expMs) return; // no exp claim — nothing to enforce client-side
    // Unified login — /auth/login handles students (roll number) and
    // staff/TPO/admins (email) in one page.
    const loginPath = '/auth/login';
    const checkExpiry = () => {
      if (Date.now() >= expMs) logout(loginPath);
    };
    checkExpiry(); // immediate, in case we mounted past expiry
    const id = window.setInterval(checkExpiry, 60_000);
    const onWake = () => { if (document.visibilityState !== 'hidden') checkExpiry(); };
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [authState.isAuthenticated, authState.token, authState.user, logout]);

  const verifyEmail = useCallback(async (data: VerifyEmailData): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post(config.api.endpoints.verifyEmail, data);

      if (response.message === "Email verified successfully") {
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Email verification failed' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, [api]);

  const resendVerification = useCallback(async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post(config.api.endpoints.resendVerification, { email });

      if (response.message === "Verification email sent successfully") {
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Failed to send verification email' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, [api]);

  const forgotPassword = useCallback(async (data: ForgotPasswordData): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post(config.api.endpoints.forgotPassword, data);

      if (response.message && response.message.includes("password reset link has been sent")) {
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Failed to send reset email' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, [api]);

  const resetPassword = useCallback(async (data: ResetPasswordData): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post(config.api.endpoints.resetPassword, data);

      if (response.message === "Password reset successfully") {
        return { success: true };
      } else {
        return { success: false, error: response.message || 'Password reset failed' };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }, [api]);


  const value: AuthContextType = {
    ...authState,
    login,
    register,
    logout,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};